import React, { useState } from 'react';
import { Box, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { toast } from 'react-toastify';
import { API } from '../../api';
import ConfirmDialog from '../DialogUi/ConfirmDialog';
import ActionPill from '../../pages/new-designs/tabs/aimd-v2/components/ActionPill';
import VideoBuilderDialog from './VideoBuilderDialog';
import { formatClock } from './duration';
import { vb } from './theme';
import { MODE_LABEL, MODE_NOUN, isTerminalJob } from './types';
import { useLessonVideo } from './useLessonVideo';
import { apiMessage } from './useAutosave';

// The lesson editor's section styles (Lesson Body, Case Studies, Roleplay, Quiz),
// so the video card reads as one more lesson mechanic.
const sectionBoxSx = {
  border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
  borderRadius: '12px',
  background: 'rgba(255,255,255,0.02)',
  p: 2.5
} as const;
const sectionTitleSx = {
  fontSize: 15,
  fontWeight: 600,
  fontFamily: 'Inter',
  color: 'var(--Text-text-tertiary, #868686)'
} as const;
const itemTitleSx = {
  fontSize: 14,
  fontWeight: 700,
  fontFamily: 'Inter',
  color: 'var(--Text-text-primary, #FDFDFD)'
} as const;
const bodySx = {
  fontSize: 13,
  fontFamily: 'Inter',
  color: 'var(--Text-text-secondary, #B6B6B6)',
  lineHeight: '20px'
} as const;
const noteSx = {
  fontSize: 12,
  fontFamily: 'Inter',
  color: 'var(--Text-text-secondary, #B6B6B6)',
  lineHeight: 1.5
} as const;

const Tag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      fontFamily: 'Inter',
      fontSize: 9,
      fontWeight: 700,
      letterSpacing: '0.3px',
      lineHeight: '14px',
      px: 0.6,
      py: 0.2,
      borderRadius: '4px',
      background: 'var(--Stroke-Border-border-dim, #2A2A2A)',
      color: 'var(--Text-text-secondary, #B6B6B6)'
    }}
  >
    {children}
  </Box>
);

// "Lesson Video · BETA" section in the lesson editor (prototype screens 1, 6, 7):
// generate / creating in background / saved (review, edit, remove, stale badge).
const LessonVideoCard: React.FC<{
  programId: string;
  moduleId: string;
  lessonId: string;
  lessonName: string;
  moduleName: string;
}> = ({ programId, moduleId, lessonId, lessonName, moduleName }) => {
  const { status, loading, stalled, refetch, recheck } = useLessonVideo(programId, moduleId, lessonId);
  const [builderOpen, setBuilderOpen] = useState<null | { resume: boolean; startAt?: 'editor' | 'preview' }>(null);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [staleDismissed, setStaleDismissed] = useState(false);
  const [removing, setRemoving] = useState(false);

  const attachment = status?.attachment ?? null;
  const project = status?.project ?? null;
  const activeJob = status?.activeJob && !isTerminalJob(status.activeJob.status) ? status.activeJob : null;
  const draft =
    project && !attachment && (project.mode === 'presenter' ? project.sections.length : project.scenes.length)
      ? project
      : null;
  const mode = attachment?.mode ?? project?.mode ?? null;
  const styleName = mode ? MODE_LABEL[mode] : '';
  const editNoun = mode ? MODE_NOUN[mode] : 'video';

  const [confirmRemove, setConfirmRemove] = useState(false);
  const remove = async () => {
    setConfirmRemove(false);
    try {
      setRemoving(true);
      await API.deleteLessonVideo({ programId, moduleId, lessonId });
      toast.success('The video was removed from the lesson.');
      await refetch();
    } catch (err) {
      toast.error(apiMessage(err, 'The video could not be removed. Try again.'));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Box data-testid="lesson-video-card" sx={{ ...sectionBoxSx, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={sectionTitleSx}>Lesson Video</Typography>
          <Box
            sx={{
              background: 'var(--Stroke-highlight, #F79420)',
              color: '#fff',
              fontSize: 9,
              fontWeight: 700,
              fontFamily: 'Inter',
              px: 0.6,
              py: 0.2,
              borderRadius: '4px',
              lineHeight: '14px',
              letterSpacing: '0.3px'
            }}
          >
            BETA
          </Box>
        </Box>
        {status && (
          <Typography sx={noteSx} data-testid="allowance-meter">
            {status.allowance.used}/{status.allowance.limit} course videos used
          </Typography>
        )}
      </Box>

      {activeJob ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }} data-testid="card-creating" aria-live="polite">
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* A stalled render keeps its place on the card but stops
                  spinning: the spinner says "working on it", and it isn't. */}
              {stalled ? (
                <WarningAmberIcon sx={{ fontSize: 15, color: 'var(--Stroke-highlight, #F79420)' }} />
              ) : (
                <CircularProgress size={12} sx={{ color: 'var(--Stroke-highlight, #F79420)' }} />
              )}
              <Typography sx={itemTitleSx}>
                {stalled
                  ? `Your ${styleName || 'video'} is taking longer than expected`
                  : `${activeJob.type === 'final' ? 'Saving' : 'Creating'} your ${styleName || 'video'}...`}
              </Typography>
            </Box>
            <Typography sx={{ ...bodySx, mt: 0.5 }}>
              {stalled
                ? `We've stopped checking. Last reported: ${activeJob.progressMessage} · ${activeJob.progress}%`
                : `Runs in the background. Keep working. ${activeJob.progressMessage} · ${activeJob.progress}%`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <ActionPill
              icon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              label="Open"
              onClick={() => setBuilderOpen({ resume: true, startAt: 'preview' })}
            />
            {stalled && (
              <ActionPill
                icon={<RefreshIcon sx={{ fontSize: 14 }} />}
                label="Check Again"
                onClick={() => recheck()}
                testId="card-recheck"
              />
            )}
          </Box>
        </Box>
      ) : attachment ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }} data-testid="card-saved">
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Box
              role="button"
              tabIndex={0}
              aria-label="Play the lesson video"
              onClick={() => setPlayerOpen(true)}
              onKeyDown={(e) => e.key === 'Enter' && setPlayerOpen(true)}
              sx={{
                position: 'relative',
                width: 168,
                aspectRatio: '16 / 9',
                borderRadius: '8px',
                overflow: 'hidden',
                background: '#000',
                cursor: 'pointer',
                flexShrink: 0,
                outline: '2px solid var(--Stroke-Border-border-dim, #2A2A2A)'
              }}
            >
              {attachment.thumbnailUrl && (
                <Box
                  component="img"
                  src={attachment.thumbnailUrl}
                  alt=""
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              )}
              <Box
                sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.55)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <PlayArrowIcon sx={{ color: '#fff', fontSize: 22 }} />
                </Box>
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  right: 6,
                  bottom: 6,
                  px: 0.6,
                  borderRadius: '4px',
                  background: 'rgba(0,0,0,0.6)',
                  color: '#fff',
                  fontFamily: 'Inter',
                  fontSize: 11,
                  fontWeight: 600
                }}
              >
                {formatClock(attachment.durationSeconds)}
              </Box>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={itemTitleSx} noWrap>
                  {lessonName} · {styleName}
                </Typography>
                <Tag>{styleName.toUpperCase()}</Tag>
              </Box>
              {status?.stale && !staleDismissed ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }} data-testid="stale-badge">
                  <WarningAmberIcon sx={{ fontSize: 15, color: 'var(--Stroke-highlight, #F79420)' }} />
                  <Typography sx={{ ...bodySx, color: 'var(--Stroke-highlight, #F79420)', fontWeight: 600 }}>
                    The Lesson Body has changed since this video was generated
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Dismiss"
                    onClick={() => setStaleDismissed(true)}
                    sx={{ color: 'var(--Text-text-tertiary, #868686)', p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }} data-testid="saved-badge">
                  <CheckCircleIcon sx={{ fontSize: 15, color: 'var(--UI-Success, #4CAF50)' }} />
                  <Typography sx={bodySx}>Saved · matches Lesson Body</Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <ActionPill
              icon={<EditIcon sx={{ fontSize: 14 }} />}
              label={`Edit ${editNoun.charAt(0).toUpperCase() + editNoun.slice(1)}`}
              onClick={() => setBuilderOpen({ resume: true, startAt: 'editor' })}
              testId="card-edit"
            />
            <ActionPill
              icon={<VisibilityOutlinedIcon sx={{ fontSize: 14 }} />}
              label="Preview as Learner"
              onClick={() => setPlayerOpen(true)}
              testId="card-preview-learner"
            />
            <Box sx={{ flex: 1 }} />
            <ActionPill
              icon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
              label={removing ? 'Removing...' : 'Remove'}
              onClick={() => setConfirmRemove(true)}
              disabled={removing}
              testId="card-remove"
            />
          </Box>
          <Typography sx={{ ...noteSx, fontStyle: 'italic' }}>
            Already saved. No separate save is needed here. Editing re-opens the {editNoun}; saving again replaces this
            video and uses one more from the course allowance.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}
          data-testid="card-empty"
        >
          <Box>
            {draft && <Typography sx={{ ...itemTitleSx, mb: 0.5 }}>Continue your {MODE_NOUN[draft.mode]}</Typography>}
            <Typography sx={bodySx}>
              {draft
                ? `Your ${MODE_LABEL[draft.mode]} draft is saved. Pick up where you left off.`
                : 'Have Elliott present this lesson, or turn it into an animated explainer. Preview and refine before you save.'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <ActionPill
              icon={<AutoAwesomeIcon sx={{ fontSize: 14, color: 'var(--Stroke-highlight, #F79420)' }} />}
              label={draft ? 'Continue' : 'Generate with Elliott'}
              onClick={() => setBuilderOpen({ resume: Boolean(draft) })}
              disabled={loading || !status}
              testId="card-generate"
            />
            {draft && (
              <ActionPill
                icon={<RefreshIcon sx={{ fontSize: 14 }} />}
                label="Start Over"
                onClick={() => setBuilderOpen({ resume: false })}
                testId="card-start-over"
              />
            )}
          </Box>
        </Box>
      )}

      {builderOpen && status && (
        <VideoBuilderDialog
          open
          programId={programId}
          moduleId={moduleId}
          lessonId={lessonId}
          lessonName={lessonName}
          moduleName={moduleName}
          status={status}
          resumeProject={builderOpen.resume ? project : null}
          startAt={builderOpen.startAt}
          onClose={() => setBuilderOpen(null)}
          onChanged={() => {
            refetch();
          }}
        />
      )}

      <ConfirmDialog
        open={confirmRemove}
        title="Remove this video from the lesson?"
        message="Learners will no longer see it. Removing does not refund the course allowance; the source stays editable."
        confirmLabel="Remove"
        busy={removing}
        busyLabel="Removing..."
        onConfirm={remove}
        onClose={() => setConfirmRemove(false)}
      />
      <Dialog
        open={playerOpen}
        onClose={() => setPlayerOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { background: vb.surface, border: `1px solid ${vb.border}`, borderRadius: '14px', p: 2 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography sx={{ fontFamily: vb.font, fontSize: 15, fontWeight: 800, color: vb.text }}>
            {lessonName}
          </Typography>
          <Tag>{styleName.toUpperCase()}</Tag>
          <Box sx={{ flex: 1 }} />
          <IconButton aria-label="Close" onClick={() => setPlayerOpen(false)} sx={{ color: vb.textTertiary }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        {attachment && (
          <Box
            component="video"
            controls
            autoPlay
            preload="metadata"
            poster={attachment.thumbnailUrl ?? undefined}
            src={attachment.videoUrl}
            data-testid="learner-preview-player"
            sx={{ display: 'block', width: '100%', aspectRatio: '16 / 9', borderRadius: '10px', background: '#000' }}
          />
        )}
        <Typography sx={{ fontFamily: vb.font, fontSize: 12, color: vb.textTertiary, mt: 1 }}>
          This video was created with AI, narrated by Elliott. Content and imagery are artificially generated.
        </Typography>
      </Dialog>
    </Box>
  );
};

export default LessonVideoCard;
